// src/components/common/TimeTab.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Clock, MapPin, CheckCircle, Plus, Award, FileText, Home, AlertCircle } from 'lucide-react';
import { useLeave } from '../../contexts/LeaveContext';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useShift } from '../../contexts/ShiftContext';
import { useAuth } from '../../hooks/useAuth';
import LeaveRequestModal from './LeaveRequestModal';
import AwardCompOffModal from './AwardCompOffModal';
import LeavePolicyModal from './LeavePolicyModal';
import AttendanceRegularizationModal from './AttendanceRegularizationModal';
import WorkFromHomeModal from './WorkFromHomeModal';
import AttendanceCalendar from './AttendanceCalendar';
import leaveService from '../../services/leave.service';
import attendanceService from '../../services/attendance.service';
import { getISTComponents, toISTDateString } from '../../utils/dateHelpers';

const TimeTab = ({ profile, isOwnProfile, permissions = null, currentUser = null, initialSubTab = null }) => {
    const { checkArrivalTime, getShiftById } = useShift();
    const { user } = useAuth();

    const {
        leaveBalances,
        leaveHistory,
        pendingRequests,
        rawLeaveBalance,
        rawLeaveHistory,
        leaveStats,
        getLeaveHistoryByPeriod,
        fetchLeaveData
    } = useLeave();

    const {
        getAttendanceForPeriod,
        getAttendanceStats,
        getAttendanceRequests,
        fetchAttendance,
        attendanceRecords,
        loading: attendanceLoading
    } = useAttendance();

    // Helper functions for deduplication and data normalization
    const getRecordId = (record) => {
        return record.id || record.leave_id || record.request_id ||
               `${record.employee_id}_${record.date || record.from_date || record.attendance_date}`;
    };

    const deduplicateByKey = (array, keyFn) => {
        const seen = new Map();
        return array.filter(item => {
            const key = keyFn(item);
            if (seen.has(key)) return false;
            seen.set(key, true);
            return true;
        });
    };

    const normalizeLeaveBalance = (data) => {
        if (!data) return [];

        // Handle different response structures
        let items = [];
        if (Array.isArray(data)) {
            items = data;
        } else if (data && typeof data === 'object') {
            if (Array.isArray(data.balance)) items = data.balance;
            else if (Array.isArray(data.balances)) items = data.balances;
            else if (!Array.isArray(data) && Object.keys(data).length > 0) {
                // Convert object to array
                items = Object.keys(data).map(key => ({
                    name: key,
                    leave_type: key,
                    leave_type_id: key,
                    total_days: data[key]?.total_days ?? data[key]?.total ?? 0,
                    used_days: data[key]?.used_days ?? data[key]?.used ?? 0,
                    remaining_days: data[key]?.remaining_days ??
                        ((data[key]?.total_days ?? data[key]?.total ?? 0) -
                         (data[key]?.used_days ?? data[key]?.used ?? 0))
                }));
            }
        }

        // Deduplicate by leave type
        return deduplicateByKey(items, (item) =>
            item.leave_type || item.name || item.leave_type_id
        );
    };

    // Initialize timeSubTab from prop if provided, otherwise default to 'leave'
    const [timeSubTab, setTimeSubTab] = useState(() => {
        // Valid subtabs: 'leave', 'attendance', 'wfh'
        const validSubTabs = ['leave', 'attendance', 'wfh'];
        if (initialSubTab && validSubTabs.includes(initialSubTab)) {
            return initialSubTab;
        }
        return 'leave';
    });
    const [selectedPeriod, setSelectedPeriod] = useState('2025');

    const [showRequestLeave, setShowRequestLeave] = useState(false);
    const [showAwardCompOff, setShowAwardCompOff] = useState(false);
    const [showLeavePolicy, setShowLeavePolicy] = useState(false);
    const [showAttendanceRequest, setShowAttendanceRequest] = useState(false);
    const [showWorkFromHome, setShowWorkFromHome] = useState(false);

    const [attendanceSubTab, setAttendanceSubTab] = useState('log');
    const [attendancePeriod, setAttendancePeriod] = useState('30');

    const [currentTime, setCurrentTime] = useState(new Date());

    const [backendLeaveBalance, setBackendLeaveBalance] = useState(null);
    const [backendLeaveHistory, setBackendLeaveHistory] = useState([]);
    const [backendPendingRequests, setBackendPendingRequests] = useState([]);

    const [regularizationRequests, setRegularizationRequests] = useState([]);
    const [regularizationLoading, setRegularizationLoading] = useState(false);

    const [wfhRequests, setWfhRequests] = useState([]);
    const [wfhLoading, setWfhLoading] = useState(false);

    // State for calculated attendance stats (from actual fetched data)
    const [calculatedStats, setCalculatedStats] = useState({
        avgHoursPerDay: 0,
        onTimeArrivalPercentage: 0,
        totalWorkingDays: 0
    });

    // Get employee ID
    const employeeId = isOwnProfile ? (user?.id || 'current_user') : (profile?.id || 'current_user');

    /** Safely parse timestamps from API into Date - treats timestamps as IST */
    const parseApiTimestamp = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp instanceof Date) {
            return Number.isNaN(timestamp.getTime()) ? null : timestamp;
        }

        const trimmed = String(timestamp).trim();
        if (!trimmed) return null;

        // If already has timezone info, parse directly
        if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
            const parsed = new Date(trimmed);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        // Handle common "YYYY-MM-DD HH:mm:ss" formats as IST time
        const normalized = trimmed.replace('T', ' ');
        const localMatch = normalized.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?$/
        );

        if (localMatch) {
            const [, year, month, day, hour = '0', minute = '0', second = '0', fractional = '0'] = localMatch;
            const milliseconds = fractional
                ? Math.round(parseFloat(`0.${fractional}`) * 1000)
                : 0;

            // FIXED: Interpret timestamps without timezone as IST (+05:30)
            const pad = (n) => String(n).padStart(2, '0');
            const isoWithIST = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}.${String(milliseconds).padStart(3, '0')}+05:30`;
            const parsed = new Date(isoWithIST);

            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        // Fallback to native parsing
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    // Calculate stats from attendance data fetched by AttendanceCalendar
    const handleAttendanceDataLoaded = useCallback((attendanceData) => {
        if (!attendanceData?.monthly || !profile) {
            return;
        }

        // Filter for present days only
        const presentRecords = attendanceData.monthly.filter(r => r.status === 'present');

        if (presentRecords.length === 0) {
            setCalculatedStats({
                avgHoursPerDay: 0,
                onTimeArrivalPercentage: 0,
                totalWorkingDays: 0
            });
            return;
        }

        // Calculate AVG HRS / DAY - only count days with actual work hours
        const recordsWithHours = presentRecords.filter(r => r.work_hours && parseFloat(r.work_hours) > 0);
        const totalHours = recordsWithHours.reduce((sum, r) => sum + parseFloat(r.work_hours), 0);
        const avgHours = recordsWithHours.length > 0
            ? (totalHours / recordsWithHours.length).toFixed(1)
            : 0;

        // Calculate ON TIME ARRIVAL using employee's shift time
        const shiftStart = profile.shift_start_time; // e.g., "09:00"
        const gracePeriod = profile.shift_grace_period || 15;

        let onTimeCount = 0;
        presentRecords.forEach(record => {
            if (record.check_in_time && shiftStart) {
                const checkInTime = parseApiTimestamp(record.check_in_time);
                if (checkInTime) {
                    const [shiftHour, shiftMin] = shiftStart.split(':').map(Number);
                    const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

                    const checkInMinutes = checkInHour * 60 + checkInMin;
                    const shiftMinutes = shiftHour * 60 + shiftMin;
                    const allowedLateMinutes = shiftMinutes + gracePeriod;

                    // On-time = arrived before or within grace period
                    if (checkInMinutes <= allowedLateMinutes) {
                        onTimeCount++;
                    }
                }
            }
        });

        const onTimePercentage = presentRecords.length > 0
            ? Math.round((onTimeCount / presentRecords.length) * 100)
            : 0;

        setCalculatedStats({
            avgHoursPerDay: avgHours,
            onTimeArrivalPercentage: onTimePercentage,
            totalWorkingDays: presentRecords.length
        });
    }, [profile]);

    const fetchRegularizationRequests = useCallback(async () => {
        if (!isOwnProfile) {
            setRegularizationRequests([]);
            return;
        }
        try {
            setRegularizationLoading(true);
            const result = await attendanceService.getMyRegularizationRequests();
            if (result.success) {
                const list = result.data?.requests || result.data || [];
                setRegularizationRequests(Array.isArray(list) ? list : []);
            } else {
                // console.error('Error fetching regularization requests:', result.error);
                setRegularizationRequests([]);
            }
        } catch (error) {
            // console.error('Error fetching regularization requests:', error);
            setRegularizationRequests([]);
        } finally {
            setRegularizationLoading(false);
        }
    }, [isOwnProfile]);

    const fetchWfhRequests = useCallback(async () => {
        if (!isOwnProfile) {
            setWfhRequests([]);
            return;
        }
        try {
            setWfhLoading(true);
            const response = await attendanceService.getMyWfhRequests();
            const list = response?.requests || response || [];
            setWfhRequests(Array.isArray(list) ? list : []);
        } catch (error) {
            // console.error('Error fetching WFH requests:', error);
        } finally {
            setWfhLoading(false);
        }
    }, [isOwnProfile]);

    const handleWfhSuccess = useCallback(async () => {
        await fetchWfhRequests();
        if (isOwnProfile && employeeId) {
            await fetchAttendance(employeeId, true);
        }
    }, [fetchWfhRequests, fetchAttendance, isOwnProfile, employeeId]);

    const handleRegularizationSuccess = useCallback(async () => {
        await fetchRegularizationRequests();
        await fetchWfhRequests();
        if (isOwnProfile && employeeId) {
            await fetchAttendance(employeeId, true);
        }
    }, [fetchRegularizationRequests, fetchAttendance, isOwnProfile, employeeId, fetchWfhRequests]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (isOwnProfile) {
            fetchLeaveData(true);
        }
    }, [isOwnProfile, fetchLeaveData]);

    useEffect(() => {
        // Fetch attendance data for both own profile and when viewing others
        if (employeeId && typeof fetchAttendance === 'function') {
            // console.log('🔄 Fetching attendance for employee:', employeeId, 'isOwnProfile:', isOwnProfile);
            fetchAttendance(employeeId, true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwnProfile, employeeId]);

    useEffect(() => {
        fetchRegularizationRequests();
        fetchWfhRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Log app load activity silently (for anti-misuse tracking)
    // Only log for own profile (employees viewing their own time tab)
    useEffect(() => {
        if (isOwnProfile) {
            // Fire and forget - don't wait or handle errors
            attendanceService.logActivity('app_load', {
                deviceType: 'web',
                metadata: { page: 'TimeTab' }
            });
        }
    }, [isOwnProfile]); // Only log once when component mounts

    // Fetch backend leave balance and history data (for admin viewing others)
    useEffect(() => {
        const fetchBackendLeaveData = async () => {
            try {
                // console.log('🔄 Fetching backend leave data...', { isOwnProfile, employeeId });
                let balance, history;
                if (isOwnProfile) {
                    [balance, history] = await Promise.all([
                        leaveService.getLeaveBalance(),
                        leaveService.getLeaveHistory()
                    ]);
                } else {
                    [balance, history] = await Promise.all([
                        leaveService.getEmployeeLeaveBalance(employeeId),
                        leaveService.getEmployeeLeaveHistory(employeeId)
                    ]);
                }
                // console.log('📥 Fetched leave data:', { balance, historyCount: history?.length, history });
                setBackendLeaveBalance(balance);
                setBackendLeaveHistory(Array.isArray(history) ? history : []);
                const pending = (Array.isArray(history) ? history : []).filter(
                    (leave) => leave.status && leave.status.toLowerCase() === 'pending'
                );
                setBackendPendingRequests(pending);
            } catch (error) {
                // console.error('❌ Error fetching backend leave data:', error);
            }
        };

        // console.log('🔍 TimeTab useEffect - Leave data:', {
        //     isOwnProfile,
        //     rawLeaveBalance,
        //     rawLeaveHistoryCount: rawLeaveHistory?.length,
        //     rawLeaveHistory,
        //     pendingRequestsCount: pendingRequests?.length
        // });

        if (isOwnProfile) {
            setBackendLeaveBalance(rawLeaveBalance || null);
            setBackendLeaveHistory(Array.isArray(rawLeaveHistory) ? rawLeaveHistory : []);
            setBackendPendingRequests(Array.isArray(pendingRequests) ? pendingRequests : []);
        } else {
            fetchBackendLeaveData();
        }
    }, [isOwnProfile, employeeId, rawLeaveBalance, rawLeaveHistory, pendingRequests]);

    /** Attendance period date window - memoized to prevent infinite loops */
    const { startDate, endDate } = useMemo(() => {
        const end = new Date();
        const start = new Date();

        if (attendancePeriod === '30') {
            start.setDate(start.getDate() - 30);
        } else {
            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const monthIndex = months.indexOf(attendancePeriod);
            if (monthIndex !== -1) {
                const year = new Date().getFullYear();
                start.setFullYear(year, monthIndex, 1);
                end.setFullYear(year, monthIndex + 1, 0);
            }
        }

        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        };
    }, [attendancePeriod]);

    const attendanceData = useMemo(() => {
        const rawData = getAttendanceForPeriod?.(employeeId, startDate, endDate) || [];

        // Deduplicate by date - keep the most recent record for each date
        const dateMap = new Map();
        rawData.forEach(record => {
            const date = record.date;
            if (!dateMap.has(date)) {
                dateMap.set(date, record);
            } else {
                // If duplicate exists, keep the one with more complete data
                const existing = dateMap.get(date);
                const hasMoreData = (record.loginTime && record.logoutTime) &&
                                  (!existing.loginTime || !existing.logoutTime);
                if (hasMoreData) {
                    dateMap.set(date, record);
                }
            }
        });

        // Convert map to array and sort by date (most recent first)
        const deduplicatedData = Array.from(dateMap.values());
        return deduplicatedData.sort((a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }, [getAttendanceForPeriod, employeeId, startDate, endDate]);

    const attendanceStats = useMemo(() =>
        getAttendanceStats?.(employeeId, Number.parseInt(attendancePeriod, 10) || 30) || {
            avgHoursPerDay: 0,
            onTimeArrivalPercentage: 0
        }
    , [getAttendanceStats, employeeId, attendancePeriod]);

    // Filter attendance requests by selected period with deduplication - memoized
    const attendanceRequests = useMemo(() => {
        // Combine all sources of requests
        const allRequests = [
            ...(regularizationRequests || []),
            ...(wfhRequests || []),
            ...(isOwnProfile ? [] : (getAttendanceRequests?.(employeeId) || []))
        ];

        // Deduplicate requests by ID
        const deduplicated = deduplicateByKey(allRequests, getRecordId);

        // Filter by period
        return deduplicated.filter((request) => {
            const requestDate = request.attendance_date || request.date;
            if (!requestDate) return false;

            const reqDate = new Date(requestDate);
            const start = new Date(startDate);
            const end = new Date(endDate);

            return reqDate >= start && reqDate <= end;
        });
    }, [regularizationRequests, wfhRequests, getAttendanceRequests, employeeId, isOwnProfile, startDate, endDate]);

    // Debug logging - only log when data changes
    useEffect(() => {
        // console.log('📋 TimeTab Attendance Data:', {
        //     employeeId,
        //     isOwnProfile,
        //     period: attendancePeriod,
        //     startDate,
        //     endDate,
        //     attendanceDataCount: attendanceData.length,
        //     filteredRequestsCount: attendanceRequests.length,
        //     attendanceData: attendanceData.slice(0, 3), // First 3 records
        //     attendanceRequests: attendanceRequests.slice(0, 3), // First 3 requests
        //     regularizationRequests: regularizationRequests.slice(0, 3),
        //     getAttendanceForPeriodExists: !!getAttendanceForPeriod,
        //     getAttendanceRequestsExists: !!getAttendanceRequests
        // });
    }, [employeeId, attendancePeriod, startDate, endDate, attendanceData.length, attendanceRequests.length]);

    /** Format attendance rows for display */
    const formatAttendanceData = (records) => {
        return (records || []).map((record) => {
            // Parse date safely - handle both "YYYY-MM-DD" and ISO timestamp formats
            // Using explicit year/month/day parsing to avoid timezone issues
            let date;
            const rawDateStr = record.date;
            if (typeof rawDateStr === 'string') {
                // Extract YYYY-MM-DD portion and create local date (not UTC)
                const match = rawDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (match) {
                    date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
                } else {
                    date = new Date(rawDateStr);
                }
            } else {
                date = new Date(rawDateStr);
            }
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const formattedDate = `${dayNames[date.getDay()]}, ${date.getDate().toString().padStart(2, '0')} ${monthNames[date.getMonth()]}`;

            const hours = parseFloat(record.workingHours || 0);
            const h = Math.floor(hours);
            const m = Math.round((hours - h) * 60);

            // Use shift-based arrival checking if employee has shift assigned
            let arrivalStatus = 'N/A';
            if (record.loginTime) {
                const employeeShift = profile?.shift_id || profile?.shift;
                if (employeeShift) {
                    const arrivalCheck = checkArrivalTime(record.loginTime, employeeShift);
                    arrivalStatus = arrivalCheck?.status || 'N/A';
                } else {
                    // Fallback to 9 AM if no shift assigned
                    const hourNum = Number.parseInt(String(record.loginTime).split(':')[0], 10);
                    arrivalStatus = !Number.isNaN(hourNum) && hourNum <= 9 ? 'On Time' : 'Late';
                }
            }

            return {
                date: formattedDate,
                day: record.location || 'Office',
                visual: hours >= 8 ? 'full' : 'partial',
                arrival: arrivalStatus,
                effectiveHours: `${h}h ${m}m`,
                grossHours: `${h}h ${m}m`,
                status: record.status,
                loginTime: record.loginTime,
                logoutTime: record.logoutTime
            };
        });
    };

    const formattedAttendance = useMemo(() => formatAttendanceData(attendanceData), [attendanceData, profile, checkArrivalTime]);

    // Period filter for leave history
    const periodStart = `${selectedPeriod}-01-01`;
    const periodEnd = `${selectedPeriod}-12-31`;

    const filteredLeaveHistory = useMemo(() => {
        // Combine both sources of leave history
        const allLeaves = [
            ...(backendLeaveHistory || []),
            ...(getLeaveHistoryByPeriod?.(periodStart, periodEnd) || [])
        ];

        // Deduplicate by leave ID
        const deduplicated = deduplicateByKey(allLeaves, getRecordId);

        // Filter by period
        return deduplicated.filter((leave) => {
            const leaveFromDate = leave.from_date || leave.start_date;
            if (!leaveFromDate) return false;
            const leaveDate = new Date(leaveFromDate);
            const start = new Date(periodStart);
            const end = new Date(periodEnd);
            return leaveDate >= start && leaveDate <= end;
        }).sort((a, b) => {
            // Sort by date, most recent first
            const dateA = new Date(a.from_date || a.start_date);
            const dateB = new Date(b.from_date || b.start_date);
            return dateB.getTime() - dateA.getTime();
        });
    }, [backendLeaveHistory, getLeaveHistoryByPeriod, periodStart, periodEnd]);

    /** Real-time stats computed from approved backend leaves */
    const realTimeStats = useMemo(() => {
        // First deduplicate the leave history to prevent double counting
        const uniqueLeaves = deduplicateByKey(backendLeaveHistory || [], getRecordId);

        // Filter for approved leaves only
        const approvedLeaves = uniqueLeaves.filter(
            (leave) => String(leave.status || '').toLowerCase() === 'approved'
        );

        // Weekly pattern (Mon..Sun)
        const weeklyPattern = [
            { day: 'M', label: 'Mon', count: 0 },
            { day: 'T', label: 'Tue', count: 0 },
            { day: 'W', label: 'Wed', count: 0 },
            { day: 'T', label: 'Thu', count: 0 },
            { day: 'F', label: 'Fri', count: 0 },
            { day: 'S', label: 'Sat', count: 0 },
            { day: 'S', label: 'Sun', count: 0 }
        ];

        // Consumed types
        const consumedTypes = {};

        // Monthly stats for current year
        const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
        const monthlyStats = months.map((m) => ({ month: m, count: 0, days: 0 }));
        const currentYear = new Date().getFullYear();

        approvedLeaves.forEach((leave) => {
            const startDate = leave.from_date || leave.start_date || leave.fromDate;
            // Calculate days properly - check both days_requested and also calculate from date range
            let days = leave.days_requested || leave.total_days || leave.days || 0;

            // If days is not available, calculate from date range
            if (!days && startDate) {
                const endDate = leave.to_date || leave.end_date || leave.toDate;
                if (endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                        const diffTime = end.getTime() - start.getTime();
                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                        days = Math.max(diffDays + 1, 1);
                    }
                }
            }

            const leaveType = leave.leave_type || leave.leaveType || 'Other';

            if (startDate) {
                const d = new Date(startDate);
                // Only process if date is valid
                if (!Number.isNaN(d.getTime())) {
                    // Weekly pattern (Monday = 0)
                    const jsDay = d.getDay(); // 0 Sun .. 6 Sat
                    const adapted = jsDay === 0 ? 6 : jsDay - 1;
                    if (weeklyPattern[adapted]) weeklyPattern[adapted].count += 1;

                    // Monthly stats (current year only)
                    if (d.getFullYear() === currentYear) {
                        const mi = d.getMonth(); // 0..11
                        if (monthlyStats[mi]) {
                            monthlyStats[mi].count += 1;
                            monthlyStats[mi].days += Number(days) || 0;
                        }
                    }
                }
            }

            // Consumed types
            consumedTypes[leaveType] = (consumedTypes[leaveType] || 0) + (Number(days) || 0);
        });

        const consumedTypesArray = Object.keys(consumedTypes).map((type) => ({
            type,
            days: consumedTypes[type]
        }));

        // console.log('📈 Consumed Types:', consumedTypesArray);
        return { weeklyPattern, consumedTypes: consumedTypesArray, monthlyStats };
    }, [backendLeaveHistory]);

    /** Leave balances list + message */
    const leaveBalanceItems = useMemo(() => {
        // Prefer backend balance if present
        const candidate = backendLeaveBalance !== undefined && backendLeaveBalance !== null
            ? backendLeaveBalance
            : leaveBalances;

        return normalizeLeaveBalance(candidate);
    }, [backendLeaveBalance, leaveBalances]);

    const leaveBalanceMessage = leaveBalanceItems.length === 0
        ? (isOwnProfile ? 'No leave balances found for your profile.' : 'No leave balances set for this employee.')
        : '';

    /** Calculate leave days */
    const calculateLeaveDays = (fromDate, toDate, isHalfDay = false) => {
        if (!fromDate || !toDate) return 0;
        const start = new Date(fromDate);
        const end = new Date(toDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return 0;
        }
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const total = diffDays >= 0 ? diffDays + 1 : 1;
        return isHalfDay ? 0.5 : total;
    };

    /** Submit leave request */
    const handleLeaveRequest = async (requestData) => {
        try {
            // Calculate the days if not provided
            const totalDays = requestData.days || calculateLeaveDays(requestData.fromDate, requestData.toDate, requestData.isHalfDay);

            // Get the leave type name from the leave balance items
            const selectedLeaveType = leaveBalanceItems.find(lt =>
                lt.leave_type_id === requestData.leaveTypeId ||
                lt.id === requestData.leaveTypeId
            );

            const response = await leaveService.applyLeave({
                leaveTypeId: requestData.leaveTypeId,
                fromDate: requestData.fromDate,
                toDate: requestData.toDate,
                reason: requestData.reason,
                emergencyContact: requestData.emergencyContact
            });

            if (response?.success) {
                setShowRequestLeave(false);

                // Refresh context
                await fetchLeaveData(true);

                // Refresh backend mirrors
                const [balance, history] = await Promise.all([
                    leaveService.getLeaveBalance(),
                    leaveService.getLeaveHistory()
                ]);
                setBackendLeaveBalance(balance);
                setBackendLeaveHistory(Array.isArray(history) ? history : []);
                const pending = (Array.isArray(history) ? history : []).filter(
                    (leave) => String(leave.status || '').toLowerCase() === 'pending'
                );
                setBackendPendingRequests(pending);

                alert('Leave request submitted successfully! Your leave balance has been updated.');
            } else {
                const errorMsg = response?.error || response?.message || 'Failed to submit leave request';
                alert(errorMsg);
            }
        } catch (error) {
            // console.error('Error submitting leave request:', error);
            const errorMessage = error?.response?.data?.error || error?.message || 'Error submitting leave request. Please try again.';
            alert(errorMessage);
        }
    };

    return (
        <div>
            {/* Sub-tabs */}
            <div className="flex gap-4 mb-6 border-b dark:border-gray-700">
                <button
                    onClick={() => setTimeSubTab('leave')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        timeSubTab === 'leave'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                    Leave
                </button>
                <button
                    onClick={() => setTimeSubTab('attendance')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        timeSubTab === 'attendance'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                    Attendance
                </button>
            </div>

            {/* Leave Sub-tab */}
            {timeSubTab === 'leave' && (
                <div className="space-y-6">
                    {/* Header with actions */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <select
                                value={selectedPeriod}
                                onChange={(e) => setSelectedPeriod(e.target.value)}
                                className="px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                            >
                                <option value="2025">Jan 2025 - Dec 2025</option>
                                <option value="2024">Jan 2024 - Dec 2024</option>
                            </select>
                        </div>
                        <div className="flex gap-2">
                            {isOwnProfile && (
                                <button
                                    onClick={() => setShowRequestLeave(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                                >
                                    <Plus className="w-4 h-4" />
                                    Apply Leave
                                </button>
                            )}
                            {isOwnProfile && (
                                <button
                                    onClick={() => setShowLeavePolicy(true)}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
                                >
                                    <FileText className="w-4 h-4" />
                                    Leave Policy
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Pending Leave Requests */}
                    {(() => {
                        const pending = backendPendingRequests.length > 0 ? backendPendingRequests : (pendingRequests || []);
                        const pendingCount = pending.length;

                        return pendingCount > 0 ? (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <div className="text-yellow-600 dark:text-yellow-400 text-2xl">⏳</div>
                                    <div>
                                        <h3 className="font-semibold text-yellow-900 dark:text-yellow-300">
                                            {pendingCount} Pending Leave Request{pendingCount > 1 ? 's' : ''}
                                        </h3>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                                            Your leave requests are awaiting approval
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <div className="text-blue-600 dark:text-blue-400 text-2xl">🎉</div>
                                    <div>
                                        <h3 className="font-semibold text-blue-900 dark:text-blue-300">Hurray! No pending leave requests</h3>
                                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">Apply leave using the button above!</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Employee Leave Stats */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Employee Leave Stats</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Weekly Pattern */}
                            <div className="border dark:border-gray-700 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Weekly Pattern</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Leaves by day of week</p>
                                <div className="flex gap-2 items-end" style={{ height: '120px' }}>
                                    {realTimeStats.weeklyPattern.map((dayData, i) => {
                                        const maxCount = Math.max(...realTimeStats.weeklyPattern.map((d) => d.count), 1);
                                        const heightPercent = maxCount > 0 ? (dayData.count / maxCount) * 100 : 0;
                                        return (
                                            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                                    {dayData.count > 0 ? dayData.count : ''}
                                                </div>
                                                <div
                                                    className="w-full bg-blue-500 dark:bg-blue-600 rounded-t transition-all duration-300 hover:bg-blue-600 dark:hover:bg-blue-500"
                                                    style={{ height: `${Math.max(heightPercent, dayData.count > 0 ? 15 : 0)}%` }}
                                                    title={`${dayData.label}: ${dayData.count} leave${dayData.count !== 1 ? 's' : ''}`}
                                                ></div>
                                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{dayData.day}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Consumed Leave Types */}
                            <div className="border dark:border-gray-700 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Consumed Leave Types</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Approved leaves by type</p>
                                {realTimeStats.consumedTypes.length > 0 ? (
                                    <div className="space-y-2">
                                        {realTimeStats.consumedTypes.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center p-2 rounded bg-gray-50 dark:bg-gray-700/50">
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{item.type}</span>
                                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                          {item.days} day{item.days !== 1 ? 's' : ''}
                        </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-24">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No approved leaves yet</p>
                                    </div>
                                )}
                            </div>

                            {/* Monthly Stats */}
                            <div className="border dark:border-gray-700 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                                    Monthly Stats ({new Date().getFullYear()})
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Total days taken per month</p>
                                <div className="flex gap-1 items-end" style={{ height: '120px' }}>
                                    {realTimeStats.monthlyStats.map((monthData, i) => {
                                        const maxDays = Math.max(...realTimeStats.monthlyStats.map((m) => m.days), 1);
                                        const heightPercent = maxDays > 0 ? (monthData.days / maxDays) * 100 : 0;
                                        return (
                                            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                                    {monthData.days > 0 ? monthData.days : ''}
                                                </div>
                                                <div
                                                    className="w-full bg-gradient-to-t from-green-500 to-green-400 dark:from-green-600 dark:to-green-500 rounded-t transition-all duration-300 hover:from-green-600 hover:to-green-500"
                                                    style={{ height: `${Math.max(heightPercent, monthData.days > 0 ? 15 : 0)}%` }}
                                                ></div>
                                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 rotate-0">
                                                    {monthData.month.substring(0, 1)}
                                                </div>

                                                {/* Tooltip */}
                                                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                                    {monthData.month}: {monthData.count} leave{monthData.count !== 1 ? 's' : ''} ({monthData.days}{' '}
                                                    day{monthData.days !== 1 ? 's' : ''})
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Leave Balances */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leave Balance</h3>
                        {leaveBalanceItems.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {leaveBalanceItems.map((balance, index) => {
                                        const colors = [
                                            { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', bold: 'text-blue-900 dark:text-blue-100', border: 'border-blue-200 dark:border-blue-700' },
                                            { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', bold: 'text-green-900 dark:text-green-100', border: 'border-green-200 dark:border-green-700' },
                                            { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', bold: 'text-purple-900 dark:text-purple-100', border: 'border-purple-200 dark:border-purple-700' },
                                            { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', bold: 'text-indigo-900 dark:text-indigo-100', border: 'border-indigo-200 dark:border-indigo-700' },
                                            { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-700 dark:text-pink-300', bold: 'text-pink-900 dark:text-pink-100', border: 'border-pink-200 dark:border-pink-700' },
                                            { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', bold: 'text-yellow-900 dark:text-yellow-100', border: 'border-yellow-200 dark:border-yellow-700' }
                                        ];
                                        const color = colors[index % colors.length];
                                        const leaveType = balance.name || balance.leave_type;
                                        const total = balance.total_days ?? balance.total ?? 0;
                                        const used = balance.used_days ?? balance.used ?? 0;
                                        const available = balance.remaining_days ?? (total - used);

                                        return (
                                            <div key={`${leaveType}-${index}`} className={`border ${color.border} rounded-lg p-4 ${color.bg}`}>
                                                <div className="text-center">
                                                    <p className={`text-xs font-medium ${color.text} mb-2`}>{leaveType}</p>
                                                    <p className={`text-2xl font-bold ${color.bold}`}>{available}/{total}</p>
                                                    <p className={`text-xs ${color.text} mt-1`}>{used} used</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                                    * Leave balances show remaining/total days
                                </p>
                            </>
                        ) : (
                            <div className="border dark:border-gray-700 rounded-lg p-8 text-center">
                                <p className="text-gray-500 dark:text-gray-400">
                                    {leaveBalanceMessage}
                                </p>
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                                    Please contact HR to set up your leave plan
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Leave History */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leave History</h3>
                        {filteredLeaveHistory.length > 0 ? (
                            <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">To</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Days</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredLeaveHistory.map((leave, idx) => {
                                        const leaveType = leave.leave_type || leave.leaveType || 'N/A';
                                        const fromDate = leave.from_date || leave.start_date || leave.fromDate;
                                        const toDate = leave.to_date || leave.end_date || leave.toDate;
                                        const days = leave.days_requested || leave.total_days || leave.days || 0;
                                        const status = leave.status || 'Pending';
                                        const reason = leave.reason || '';

                                        return (
                                            <tr key={leave.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{leaveType}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {fromDate ? toISTDateString(new Date(fromDate)) : 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {toDate ? toISTDateString(new Date(toDate)) : 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{days}</td>
                                                <td className="px-4 py-3">
                            <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                                    String(status).toLowerCase() === 'approved'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                        : String(status).toLowerCase() === 'pending'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                }`}
                            >
                              {status}
                            </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{reason}</td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-8 text-center">
                                <p className="text-teal-700 dark:text-teal-300">No Leave history to show</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Attendance Sub-tab */}
            {timeSubTab === 'attendance' && (
                <div className="space-y-6">
                    {/* Attendance Stats */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="border dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {profile?.first_name?.[0]}
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">Me</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">AVG HRS / DAY</div>
                                    <div className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {calculatedStats.avgHoursPerDay}h
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">ON TIME ARRIVAL</div>
                                    <div className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {calculatedStats.onTimeArrivalPercentage}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Current Time & Actions */}
                    <div className="border dark:border-gray-700 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Actions</h3>
                        <div className="flex items-center gap-4">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {(() => {
                                    const { hour, minute, second } = getISTComponents(currentTime);
                                    const hour12 = hour % 12 || 12;
                                    const ampm = hour >= 12 ? 'PM' : 'AM';
                                    return `${hour12}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')} ${ampm} IST`;
                                })()}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                {(() => {
                                    const { date, year, month, day } = getISTComponents(currentTime);
                                    const dateObj = new Date(year, month - 1, day);
                                    const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Kolkata' });
                                    const monthName = dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'Asia/Kolkata' });
                                    return `${weekday}, ${String(day).padStart(2, '0')} ${monthName} ${year}`;
                                })()}
                            </div>
                            <div className="ml-auto flex gap-2">
                                <button
                                    onClick={() => setShowWorkFromHome(true)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                    <Home className="w-4 h-4" />
                                    Work From Home
                                </button>
                                <button
                                    onClick={() => setShowAttendanceRequest(true)}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                                    Attendance Request
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Logs & Requests */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Logs & Requests</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setAttendanceSubTab('log')}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg ${
                                        attendanceSubTab === 'log'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    Attendance Log
                                    {formattedAttendance.length > 0 && (
                                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                            attendanceSubTab === 'log'
                                                ? 'bg-blue-700'
                                                : 'bg-gray-200 dark:bg-gray-600'
                                        }`}>
                                            {formattedAttendance.length}
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => setAttendanceSubTab('requests')}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg ${
                                        attendanceSubTab === 'requests'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    Attendance Requests
                                    {attendanceRequests.length > 0 && (
                                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                            attendanceSubTab === 'requests'
                                                ? 'bg-blue-700'
                                                : 'bg-gray-200 dark:bg-gray-600'
                                        }`}>
                                            {attendanceRequests.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Period Selector - Only show for Attendance Requests tab (calendar has its own navigation) */}
                        {attendanceSubTab === 'requests' && (
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setAttendancePeriod('30')}
                                    className={`px-4 py-2 text-sm rounded ${
                                        attendancePeriod === '30'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    30 DAYS
                                </button>
                                {['SEP', 'AUG', 'JUL', 'JUN', 'MAY', 'APR'].map((month) => (
                                    <button
                                        key={month}
                                        onClick={() => setAttendancePeriod(month)}
                                        className={`px-4 py-2 text-sm rounded ${
                                            attendancePeriod === month
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                        }`}
                                    >
                                        {month}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Attendance Calendar */}
                        {attendanceSubTab === 'log' && (
                            <div className="border dark:border-gray-700 rounded-lg p-4">
                                <AttendanceCalendar
                                    employeeId={employeeId}
                                    employeeDetails={profile}
                                    isOwnProfile={isOwnProfile}
                                    showFilters={true}
                                    onDataLoaded={handleAttendanceDataLoaded}
                                    isAdmin={!isOwnProfile}
                                />
                            </div>
                        )}

                        {/* Attendance Requests Tab */}
                        {attendanceSubTab === 'requests' && (
                            <div className="border dark:border-gray-700 rounded-lg p-6">
                                {attendanceRequests.length > 0 ? (
                                    <div className="space-y-4">
                                        {attendanceRequests.map((request) => {
                                            if (isOwnProfile) {
                                                const attendanceDate = parseApiTimestamp(request.attendance_date);
                                                const punchIn = parseApiTimestamp(request.requested_punch_in);
                                                const punchOut = parseApiTimestamp(request.requested_punch_out);
                                                return (
                                                    <div key={request.id} className="border dark:border-gray-600 rounded-lg p-4">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h4 className="font-semibold text-gray-900 dark:text-white capitalize">
                                                                    {request.regularization_type?.replace('_', ' ') || 'Regularization'}
                                                                </h4>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                                    {attendanceDate
                                                                        ? (() => {
                                                                            const { year, month, day } = getISTComponents(attendanceDate);
                                                                            const dateObj = new Date(year, month - 1, day);
                                                                            const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Asia/Kolkata' });
                                                                            const monthName = dateObj.toLocaleDateString('en-US', { month: 'long', timeZone: 'Asia/Kolkata' });
                                                                            return `${weekday}, ${monthName} ${day}, ${year}`;
                                                                        })()
                                                                        : '--'}
                                                                </p>
                                                                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                                                    <p>
                                                                        Check In:{' '}
                                                                        {punchIn
                                                                            ? (() => {
                                                                                const { hour, minute } = getISTComponents(punchIn);
                                                                                const hour12 = hour % 12 || 12;
                                                                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                                                                return `${hour12}:${String(minute).padStart(2, '0')} ${ampm} IST`;
                                                                            })()
                                                                            : '--'}
                                                                    </p>
                                                                    <p>
                                                                        Check Out:{' '}
                                                                        {punchOut
                                                                            ? (() => {
                                                                                const { hour, minute } = getISTComponents(punchOut);
                                                                                const hour12 = hour % 12 || 12;
                                                                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                                                                return `${hour12}:${String(minute).padStart(2, '0')} ${ampm} IST`;
                                                                            })()
                                                                            : '--'}
                                                                    </p>
                                                                </div>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                                    {request.reason}
                                                                </p>
                                                                {request.manager_comments && (
                                                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                                                                        Manager Note: {request.manager_comments}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <span
                                                                className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                                    request.status === 'approved'
                                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                                        : request.status === 'rejected'
                                                                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                                }`}
                                                            >
                                {request.status}
                              </span>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            // Manager/admin view of list from context
                                            return (
                                                <div key={request.id} className="border dark:border-gray-600 rounded-lg p-4">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-semibold text-gray-900 dark:text-white">{request.requestType}</h4>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{request.date}</p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{request.reason}</p>
                                                        </div>
                                                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                              {request.status}
                            </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                        <p className="text-gray-500 dark:text-gray-400">
                                            {regularizationLoading && isOwnProfile
                                                ? 'Loading attendance regularizations…'
                                                : 'No attendance requests for the selected period'}
                                        </p>
                                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                            {!regularizationLoading && 'Try selecting a different period to view requests'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showRequestLeave && (
                <LeaveRequestModal
                    leaveTypes={leaveBalanceItems}
                    onClose={() => setShowRequestLeave(false)}
                    onSubmit={handleLeaveRequest}
                />
            )}

            <AwardCompOffModal
                isOpen={showAwardCompOff}
                onClose={() => setShowAwardCompOff(false)}
                profile={profile}
            />

            <LeavePolicyModal
                isOpen={showLeavePolicy}
                onClose={() => setShowLeavePolicy(false)}
            />

            <AttendanceRegularizationModal
                isOpen={showAttendanceRequest}
                onClose={() => setShowAttendanceRequest(false)}
                managerId={
                    profile?.reporting_manager_id ||
                    profile?.managerId ||
                    currentUser?.reporting_manager_id ||
                    currentUser?.managerId ||
                    user?.reporting_manager_id ||
                    user?.managerId ||
                    null
                }
                onSubmitSuccess={handleRegularizationSuccess}
                shiftStartTime={profile?.shift_start_time || currentUser?.shift_start_time}
                shiftEndTime={profile?.shift_end_time || currentUser?.shift_end_time}
            />

            <WorkFromHomeModal
                isOpen={showWorkFromHome}
                onClose={() => setShowWorkFromHome(false)}
                managerId={
                    profile?.reporting_manager_id ||
                    profile?.managerId ||
                    currentUser?.reporting_manager_id ||
                    currentUser?.managerId ||
                    user?.reporting_manager_id ||
                    user?.managerId ||
                    null
                }
                onSubmitSuccess={handleWfhSuccess}
            />
        </div>
    );
};

export default TimeTab;
